import React from 'react';
import { Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Accordion from 'components/Accordion';
import Button from 'components/DesignSystem/Button';
import EnsembleDialog from 'components/Meteorology/Weather/Similarity/EnsembleDialog';
import {
  selectSimilaridadeEnsembleData,
  selectSimilaridadeEnsembleModalOpen,
  setEnsembleModalOpen,
  setEnsembleData,
  reset,
  selectSimilaridadeCanGenerateEnsemble,
  selectSimilaridadeCanGenerateMaps,
  selectSimilaridadeVariavel,
  selectSimilaridadeMapsData,
  selectSimilaridadeAnos,
  selectSimilaridadeMeses,
  selectSimilaridadeMapsLoading,
  setMapsLoading,
  setMapsData,
} from 'redux/reducers/similaridadeReducer';
import { useGetFilteredYearsMapsMutation, useGenerateEnsembleMutation } from 'services/api/Meteorology/Weather/Similarity';
import { MapData } from 'types/Meteorology/indicesClimaticos';
import Variable from './Variable';
import './index.scss';

export default function SimilarityControlPanel() {
  const dispatch = useDispatch();
  const ensembleData = useSelector(selectSimilaridadeEnsembleData);
  const isModalOpen = useSelector(selectSimilaridadeEnsembleModalOpen);
  const canGenerateEnsemble = useSelector(selectSimilaridadeCanGenerateEnsemble);
  const canGenerateMaps = useSelector(selectSimilaridadeCanGenerateMaps);
  const variavel = useSelector(selectSimilaridadeVariavel);
  const mapsData = useSelector(selectSimilaridadeMapsData);
  const anos = useSelector(selectSimilaridadeAnos);
  const meses = useSelector(selectSimilaridadeMeses);
  const isLoadingMaps = useSelector(selectSimilaridadeMapsLoading);

  const [getFilteredMaps] = useGetFilteredYearsMapsMutation();
  const [generateEnsemble, { isLoading: generatingEnsemble }] = useGenerateEnsembleMutation();

  const handleGenerateMaps = async () => {
    if (!canGenerateMaps) return;

    dispatch(setMapsLoading(true));

    try {
      // Fazer todas as chamadas de API em paralelo
      const promises = meses.map((mes) => getFilteredMaps({
        variavel,
        mes,
        anos,
      }).unwrap()
        .then((result) => {
          if (result && result.length > 0) {
            // Converter AnomalyMap para MapData, incluindo o mês
            return result.map((map) => ({
              nome: map.nome,
              ano: map.ano,
              mes,
              base64_data: '',
              link: map.link,
              variavel,
            }));
          }
          return [];
        })
        .catch(() => []));

      const results = await Promise.all(promises);
      const allMaps: MapData[] = results.flat();

      // Atualizar o Redux com todos os mapas de uma vez
      dispatch(setMapsData(allMaps));
    } catch (error) {
      // Handle error and reset loading state
      dispatch(setMapsLoading(false));
    }
  };

  const handleGenerateEnsemble = async () => {
    if (!canGenerateEnsemble || mapsData.length === 0) return;

    // Extrair anos e mês dos mapas gerados
    const anosEnsemble = Array.from(new Set(mapsData.map((map) => map.ano)))
      .map((ano) => Number(ano))
      .sort((a, b) => a - b);
    const mes = mapsData[0]?.mes || 1;

    try {
      // Fazer chamada API REAL para gerar ensemble
      const requestPayload = {
        dict_request: {
          variavel,
          mes: Number(mes),
          anos: anosEnsemble,
        },
        email: '', // Será preenchido automaticamente pela API
      };

      const result = await generateEnsemble(requestPayload).unwrap();

      if (result && result.url) {
        // Converter dados do resultado para o formato esperado pelo EnsembleDialog
        const ensembleDataFormatted = {
          nome: `Ensemble ${variavel}`,
          variavel: result.var || variavel,
          mes: typeof result.mes === 'string' ? parseInt(result.mes, 10) : Number(result.mes) || mes,
          anos: Array.isArray(result.anos) ? result.anos : anosEnsemble,
          base64_data: '',
          url: result.url,
        };

        // eslint-disable-next-line no-console
        console.log('Dados formatados para o modal:', ensembleDataFormatted);

        dispatch(setEnsembleData(ensembleDataFormatted as any));
        dispatch(setEnsembleModalOpen(true));
      } else {
        // eslint-disable-next-line no-alert
        alert('Resposta da API não contém URL da imagem.');
      }
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('ControlPanel - Erro ao gerar ensemble:', error);
      const errorMessage = error?.data?.message || error?.message || 'Erro ao gerar ensemble. Por favor, tente novamente.';
      // eslint-disable-next-line no-alert
      alert(errorMessage);
    }
  };

  const handleClearFilters = () => {
    dispatch(reset());
  };

  const handleCloseModal = () => {
    dispatch(setEnsembleModalOpen(false));
  };

  return (
    <div>
      <Accordion defaultActiveKey="0">
        <Variable />
      </Accordion>
      <Row className="pt-5">
        <Button
          id="similarity-generate-maps-button"
          type="primary"
          onClick={handleGenerateMaps}
          disabled={!canGenerateMaps || isLoadingMaps}
        >
          {isLoadingMaps ? 'Gerando Mapas...' : 'Gerar Mapas'}
        </Button>
      </Row>
      <Row className="pt-1">
        <Button
          id="similarity-generate-ensemble-button"
          type="primary"
          onClick={handleGenerateEnsemble}
          disabled={!canGenerateEnsemble || generatingEnsemble}
        >
          {generatingEnsemble ? 'Gerando Ensemble...' : 'Gerar Ensemble'}
        </Button>
      </Row>
      <Row>
        <Button type="secondary" className="clear-filters mt-2" onClick={handleClearFilters}>
          Limpar filtros
        </Button>
      </Row>

      <EnsembleDialog
        show={isModalOpen}
        onHide={handleCloseModal}
        ensembleData={ensembleData}
      />
    </div>
  );
}
